<template>
  <section class="cpp card mb-0">
    <header class="cpp__head">
      <b>Дополнительные точки ({{ points.length }})</b>
      <button type="button" class="btn btn-link btn-sm p-0 cpp__toggle" :aria-expanded="String(open)" @click="$emit('toggle')">
        {{ open ? 'Свернуть' : 'Развернуть' }}
      </button>
    </header>
    <div v-if="open" class="cpp__body">
      <div class="cpp__actions">
        <button type="button" class="btn btn-sm btn-success cpp__add-point" :disabled="!canEditPoints" @click="$emit('add-point')">Новая точка</button>
        <button type="button" class="btn btn-sm btn-outline-secondary cpp__add-group" :disabled="groupsStatus !== 'ready'" @click="$emit('add-group')">Новая группа</button>
        <label class="cpp__layer-label ml-auto mb-0">
          <input type="checkbox" class="cpp__layer" :checked="layerOn" @change="$emit('toggle-layer')"> слой
        </label>
      </div>
      <div v-if="!canEditPoints" class="small text-muted">Создание и изменение точек доступно после загрузки групп и точек.</div>

      <div v-if="groupsStatus === 'error'" class="cpp__state text-danger">
        Группы не загрузились
        <button type="button" class="btn btn-sm btn-light ml-2 cpp__retry" @click="$emit('retry')">Повторить</button>
      </div>
      <div v-if="pointsStatus === 'error'" class="cpp__state text-danger">Точки не загрузились</div>

      <template v-if="groupsStatus === 'ready'">
        <div v-for="g in groups" :key="g.id" class="cpp__group">
          <div class="cpp__group-row">
            <span class="cpp__diamond" :style="{ background: colorOf(g) }"></span>
            <b class="cpp__group-title">{{ g.title || '—' }}</b>
            <span class="text-muted small">{{ pointsOf(g.id).length }} т.</span>
            <button type="button" class="btn btn-link btn-sm p-0 ml-auto cpp__group-toggle" @click="$emit('toggle-group', g.id)">
              {{ hiddenGroupIds.includes(g.id) ? 'Показать' : 'Скрыть' }}
            </button>
            <button type="button" class="btn btn-link btn-sm p-0 cpp__group-edit" aria-label="Изменить группу" @click="$emit('edit-group', g)"><i class="ri-pencil-line"></i></button>
            <button type="button" class="btn btn-link btn-sm p-0 text-danger cpp__group-delete" aria-label="Удалить группу" @click="$emit('delete-group', g)"><i class="ri-delete-bin-line"></i></button>
          </div>
          <div v-for="p in pointsOf(g.id)" :key="p.id" class="cpp__point">
            <PointRow :point="p" :can-edit="canEditPoints" @focus="$emit('focus-point', p)" @edit="$emit('edit-point', p)" @delete="$emit('delete-point', p)" />
          </div>
        </div>
        <div v-if="orphans.length" class="cpp__group">
          <div class="cpp__group-row">
            <span class="cpp__diamond" :style="{ background: ungroupedColor }"></span>
            <b class="cpp__group-title">Без группы</b>
            <span class="text-muted small">{{ orphans.length }} т.</span>
          </div>
          <div v-for="p in orphans" :key="p.id" class="cpp__point">
            <PointRow :point="p" :can-edit="canEditPoints" @focus="$emit('focus-point', p)" @edit="$emit('edit-point', p)" @delete="$emit('delete-point', p)" />
          </div>
        </div>
      </template>
      <template v-else>
        <div v-for="p in points" :key="p.id" class="cpp__point">
          <PointRow :point="p" :can-edit="canEditPoints" @focus="$emit('focus-point', p)" @edit="$emit('edit-point', p)" @delete="$emit('delete-point', p)" />
        </div>
      </template>
      <div v-if="pointsStatus === 'ready' && points.length === 0" class="cpp__state text-muted">Дополнительных точек нет</div>
    </div>
  </section>
</template>

<script>
import { groupColor, hasValidCoords, UNGROUPED_COLOR } from '@/components/management/dispatcher/objectState';

const PointRow = {
  name: 'PointRow',
  props: { point: { type: Object, required: true }, canEdit: { type: Boolean, default: true } },
  computed: { focusable() { return hasValidCoords(this.point); } },
  render(h) {
    const p = this.point;
    return h('div', { class: 'cpp__point-row' }, [
      h('div', { class: 'cpp__point-main' }, [
        h('div', { class: 'cpp__point-name' }, p.name || '—'),
        h('div', { class: 'text-muted small' }, p.address || 'Адрес не указан'),
      ]),
      h('div', { class: 'cpp__point-actions' }, [
        h('button', { class: 'btn btn-sm btn-outline-secondary cpp__focus', attrs: { type: 'button', disabled: !this.focusable }, on: { click: () => this.$emit('focus') } }, 'На карте'),
        !this.focusable ? h('span', { class: 'small text-muted' }, 'нет на карте') : null,
        h('button', { class: 'btn btn-link btn-sm p-0 cpp__point-edit', attrs: { type: 'button', 'aria-label': 'Изменить точку', disabled: !this.canEdit }, on: { click: () => this.$emit('edit') } }, [h('i', { class: 'ri-pencil-line' })]),
        h('button', { class: 'btn btn-link btn-sm p-0 text-danger cpp__point-delete', attrs: { type: 'button', 'aria-label': 'Удалить точку' }, on: { click: () => this.$emit('delete') } }, [h('i', { class: 'ri-delete-bin-line' })]),
      ]),
    ]);
  },
};

export default {
  name: 'CustomPointsPanel',
  components: { PointRow },
  props: {
    groups: { type: Array, default: () => [] },
    points: { type: Array, default: () => [] },
    groupsStatus: { type: String, default: 'idle' },
    pointsStatus: { type: String, default: 'idle' },
    hiddenGroupIds: { type: Array, default: () => [] },
    layerOn: { type: Boolean, default: true },
    open: { type: Boolean, default: false },
  },
  data() { return { ungroupedColor: UNGROUPED_COLOR }; },
  computed: {
    canEditPoints() { return this.groupsStatus === 'ready' && this.pointsStatus === 'ready'; },
    groupIds() { return this.groups.map((g) => g.id); },
    orphans() { return this.points.filter((p) => !this.groupIds.includes(p.group_id)); },
  },
  methods: {
    colorOf(group) { return groupColor(group); },
    pointsOf(groupId) { return this.points.filter((p) => p.group_id === groupId); },
  },
};
</script>

<style scoped lang="scss">
.cpp { border: 1px solid #e3e5e1; border-radius: 10px; }
.cpp__head { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; border-bottom: 1px solid #eceee9; font-size: 13px; }
.cpp__body { padding: 8px 12px; display: flex; flex-direction: column; gap: 8px; font-size: 12.5px; }
.cpp__actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.cpp__layer-label { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #4c524d; cursor: pointer; }
.cpp__state { padding: 4px 0; }
.cpp__group { border: 1px solid #eceee9; border-radius: 9px; padding: 8px 10px; }
.cpp__group-row { display: flex; align-items: center; gap: 8px; }
.cpp__diamond { width: 11px; height: 11px; transform: rotate(45deg); flex: none; }
.cpp__point { border-top: 1px dashed #eceee9; margin-top: 6px; padding-top: 6px; }
</style>

<style lang="scss">
/* PointRow is a render-function child; keep its styles global but namespaced */
.cpp__point-row { display: flex; flex-direction: column; gap: 4px; }
.cpp__point-name { font-weight: 700; }
.cpp__point-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
</style>
